<template>
  <Card v-if="isPayCompleted">
    <VRow>
      <VCol>
        <ThankYou>
          <template v-slot:title>
            <span class="text-main-color">Payment Made - Thanks!</span>
          </template>
          <template v-slot:text>
            <div v-if="isAppCreateLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
              <VProgressCircular indeterminate color="primary" />
            </div>
            <template v-else>
              <VAlert v-if="isAppCreateError" type="warning" class="my-4">
                If you see this message, then something has gone wrong. Please contact us. And tell us your transaction
                &nbsp; id (<b>{{ payCompletedId }}</b
                >)
              </VAlert>
              <p v-if="applicationId && !isAppCreateError" class="my-4">
                Next Step 1 -
                <router-link :to="{ name: 'Short Form Page', params: { id: applicationId } }" target="_blank"
                  ><b>Provide Basic Info Now</b></router-link
                >
              </p>
            </template>
            <VDivider class="my-4" />
            <p class="my-4">
              Next Step 2 - <a :href="bookLink" target="_blank"><b>Book a call Here</b></a> &nbsp;<i
                >(in 2 working days)</i
              >
            </p>
            <VDivider class="my-4" />
            <p class="my-4">
              <b>Emails Just Sent Now</b>
            </p>
            <p class="my-4">A - Payment receipt has been sent to email(s) provided.</p>
            <p class="my-4">
              <b><u>B - Email just sent with Claim ref number (it starts with 'C') & how our process works.</u></b>
            </p>
            <p><i>(Check junk/spam folder too)</i>.</p>
          </template>
        </ThankYou>
      </VCol>
    </VRow>
  </Card>

  <template v-else>
    <h1 class="text-main-color my-3">Welcome to Missed UK State Pension Claims</h1>
    <Card>
      <VRow>
        <VCol cols="12">
          <div class="d-flex flex-column align-center justify-center w-100 ga-3">
            <h1 class="text-title-color font-size-18 font-weight-700">Payment details</h1>
          </div>
        </VCol>
        <VCol v-if="!isCountrySelected" cols="12">
          <div class="d-flex flex-column align-center justify-center ga-2">
            <div class="text-subtitle-color font-size-14 font-weight-500">Click where you live now:</div>
            <VRow>
              <template v-for="country of countryList" :key="country.name">
                <VCol cols="12" sm="4">
                  <div
                    @click="handleSelectCountry(country)"
                    class="d-flex flex-column align-center justify-center country-list-container__item"
                  >
                    <img :src="country.image" :alt="country.name" />
                    <div>{{ country.name }}</div>
                  </div>
                </VCol>
              </template>
            </VRow>
          </div>
        </VCol>
      </VRow>
      <template v-if="isCountrySelected">
        <template v-if="isStripeLoaded">
          <VForm v-model="formValid" @submit.prevent>
            <VRow>
              <VCol cols="12">
                <VTextField
                  name="email"
                  label="Your Email"
                  type="email"
                  variant="outlined"
                  color="primary"
                  placeholder="e.g. name@example.com"
                  v-model="email"
                  :disabled="isPayLoading"
                  :rules="formRules.email"
                  class="custom-input"
                />
              </VCol>
              <VCol cols="12">
                <div class="text-title-color font-size-16 font-weight-600 mb-3">
                  HELPER: Friend / Family for Communication
                </div>
              </VCol>
              <VCol cols="12">
                <VRow>
                  <VCol cols="12" sm="6">
                    <VTextField
                      name="friendFirstName"
                      v-capitalize
                      v-model="friendFirstName"
                      label="Friend/Family First name"
                      placeholder="e.g. John"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      name="friendLastName"
                      v-capitalize
                      v-model="friendLastName"
                      label="Friend/Family Last name"
                      placeholder="e.g. Doe"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <div class="d-flex align-center">
                      <VSelect
                        v-model="selectedFriendPhoneCountry"
                        :items="phoneCodeList"
                        item-title="label"
                        item-value="id"
                        variant="outlined"
                        density="comfortable"
                        style="max-width: 140px"
                        class="mr-2"
                        :disabled="isPayLoading"
                      />
                      <VTextField
                        name="friendPhoneNumber"
                        v-model="friendPhoneNumber"
                        label="Phone Number"
                        variant="outlined"
                        density="comfortable"
                        :disabled="isPayLoading"
                        placeholder="e.g. 1234567890"
                        class="flex-grow-1"
                        :rules="[formRules.onlyDigits]"
                      />
                    </div>
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      name="friendRelationship"
                      v-capitalize
                      v-model="friendRelationship"
                      label="Friend/Family Relationship"
                      placeholder="e.g. Daughter / Friend"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      name="friendFamilyEmail"
                      label="Friend/Family Email cc'd"
                      variant="outlined"
                      density="comfortable"
                      color="primary"
                      type="email"
                      placeholder="e.g. name@example.com"
                      v-model="friendFamilyEmail"
                      :disabled="isPayLoading"
                      :rules="formRules.friendEmail"
                      class="custom-input"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VDivider class="my-3" />
              <VCol cols="12">
                <div class="text-title-color font-size-16 font-weight-600 mb-3">Personal Details</div>
                <div>
                  <VSelect
                    name="salutation"
                    v-model="title"
                    label="Title"
                    :items="titleOptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[formRules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </div>
                <div id="address-element"></div>
              </VCol>

              <!-- Address Validation Status -->
              <VCol cols="12" v-if="addressValidationResult">
                <div class="d-flex align-center gap-2 flex-wrap">
                  <VChip
                    :color="addressValidationResult.isValid ? 'success' : 'error'"
                    size="small"
                    class="address-validation-chip"
                  >
                    <VIcon size="small" left>
                      {{ addressValidationResult.isValid ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </VIcon>
                    {{ addressValidationResult.isValid ? 'Address Verified ✓' : 'Address Invalid ✗' }}
                  </VChip>
                  <span v-if="addressValidationResult.message" class="text-caption text-grey">
                    {{ addressValidationResult.message }}
                  </span>
                  <span v-if="isValidatingAddress" class="text-caption text-grey">
                    <VProgressCircular indeterminate size="16" width="2" class="mr-1" />
                    Validating...
                  </span>
                </div>
              </VCol>

              <VCol cols="12">
                <label>Card Number</label>
                <div id="card-element"></div>
              </VCol>
              <VCol cols="12" class="mt-3">
                <div>
                  <VTextarea
                    name="howDidYouHearAboutUs"
                    v-model.trim="howDidYouHearAboutUs"
                    label="How did you hear about us?"
                    variant="outlined"
                    maxlength="255"
                    :disabled="isPayLoading"
                    class="custom-input"
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div>
                  <VSelect
                    name="agent"
                    label="Which Agent Did You Speak to?"
                    :items="agentsOptions"
                    v-model="agent"
                    variant="outlined"
                    :disabled="isPayLoading"
                    class="custom-input"
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="d-flex flex-column align-center justify-center">
                  <VCheckbox v-model="isAgreeTermsConditions" :disabled="isPayLoading" :rules="formRules.terms">
                    <template v-slot:label>
                      <div>
                        I agree to XtraPension's
                        <a href="https://www.xtrapension.com/terms" target="_blank" v-bind="props" @click.stop>
                          Terms & Conditions</a
                        >
                      </div>
                    </template>
                  </VCheckbox>
                </div>
              </VCol>
              <VCol cols="12">
                <div v-if="isPayLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
                  <VProgressCircular indeterminate color="primary" />
                </div>
                <div class="d-flex items-center justify-center w-100">
                  <button
                    class="main-btn"
                    :disabled="isPayLoading || !isAgreeTermsConditions || !isAddressValid"
                    @click="pay()"
                    type="submit"
                  >
                    Pay {{ currencySymbol }}{{ formatAmount(amount) }}
                  </button>
                </div>
                <div v-if="stripeValidationError" class="info-container info-container__red my-2">
                  <p>{{ stripeValidationError }}</p>
                </div>
                <div v-if="addressValidationError" class="info-container info-container__red my-2">
                  <p>{{ addressValidationError }}</p>
                </div>
              </VCol>
            </VRow>
          </VForm>
        </template>
        <template v-else>
          <div class="d-flex flex-column align-center justify-center py-4 w-100">
            <VProgressCircular indeterminate color="primary" />
          </div>
        </template>
      </template>
    </Card>
  </template>
</template>

<script>
import { mapActions } from 'vuex';
// Service
import StripeService from '@/services/stripe-service/stripe.service';
import ApplicationService from '@/services/application-service/application.service';
import AddressService from '@/services/address-service/address.service';
import { toLocalISODate } from '@/utils/toLocalISODate';
// Components
import Card from '../Card/Card.vue';
import ThankYou from '../ThankYou/ThankYou.vue';

export default {
  components: {
    Card,
    ThankYou
  },
  props: {
    foundApplication: {
      type: Object
    }
  },
  inject: ['phoneCodeList'],
  computed: {
    friendTel() {
      if (!this.friendPhoneNumber) {
        return '';
      }
      const selectedCountry = this.phoneCodeList.find((c) => c.id === this.selectedFriendPhoneCountry);
      const countryCode = selectedCountry ? selectedCountry.code : null;
      return `+${countryCode}${this.friendPhoneNumber}`;
    },
    currencyCode() {
      let currency = 'USD';
      if (this.isEuropeanCountry(this.countrySelected)) {
        currency = 'EUR';
      } else if (this.countrySelected === 'CA') {
        currency = 'CAD';
      } else if (this.countrySelected === 'US') {
        currency = 'USD';
      } else if (this.countrySelected === 'AU') {
        currency = 'AUD';
      } else if (this.countrySelected === 'NZ') {
        currency = 'NZD';
      } else if (this.countrySelected === 'ZA') {
        currency = 'ZAR';
      }
      return currency;
    },
    currencySymbol() {
      let currencySymbol = 'US $';
      if (this.isEuropeanCountry(this.countrySelected)) {
        currencySymbol = '€';
      } else if (this.countrySelected === 'CA') {
        currencySymbol = 'CA $';
      } else if (this.countrySelected === 'AU') {
        currencySymbol = 'AU $';
      } else if (this.countrySelected === 'NZ') {
        currencySymbol = 'NZ $';
      } else if (this.countrySelected === 'ZA') {
        currencySymbol = 'R';
      }
      return currencySymbol;
    },
    amount() {
      let amount = 7000;
      if (this.isEuropeanCountry(this.countrySelected)) {
        amount = 5000;
      } else if (this.countrySelected === 'AU' || this.countrySelected === 'NZ' || this.countrySelected === 'CA') {
        amount = 10000;
      } else if (this.countrySelected === 'ZA') {
        amount = 100000;
      }
      return amount;
    },
    isAddressValid() {
      // Address is considered valid if:
      // 1. It has been validated and is valid, OR
      // 2. It hasn't been validated yet (skip validation requirement)
      if (this.addressValidationResult === null) return false;
      return this.addressValidationResult.isValid === true;
    },
    bookLink() {
      const query = `name=${this.fullName}&email=${this.email}&a1=${this.phone}`;
      if (this.countrySelected === 'AU' || this.countrySelected === 'NZ') {
        return encodeURI(`https://calendly.com/xtrapension/claiming-your-lump-sum-aus-nz?${query}`);
      } else {
        return encodeURI(`https://calendly.com/xtrapension/claim-validation-call-eu-row?${query}`);
      }
    },
    titleOptions() {
      const options = [
        { label: 'Choose', value: '' },
        { label: 'Mr', value: 'Mr' },
        { label: 'Mrs', value: 'Mrs' },
        { label: 'Ms', value: 'Ms' },
        { label: 'Miss', value: 'Miss' },
        { label: 'Dr', value: 'Dr' },
        { label: 'Prof', value: 'Prof' }
      ];
      return options;
    }
  },
  data() {
    return {
      isStripeLoaded: false,
      isPayLoading: false,
      isPayCompleted: false,
      applicationId: null,
      isCountrySelected: false,
      countrySelected: 'Canada',
      payCompletedId: null,
      isAppCreateLoading: false,
      isAppCreateError: false,
      stripe: null,
      cardElement: null,
      addressElement: null,
      fullName: null,
      firstName: null,
      lastName: null,
      shippingAddress: null,
      phone: null,
      email: '',
      stripeValidationError: '',
      addressValidationError: '',
      tax: true,
      customer: null,
      isAgreeTermsConditions: false,
      latest_charge: null,
      howDidYouHearAboutUs: '',
      agent: this.$route.name === 'Phone Page' ? '' : '',
      leadId: this.$route.query.leadId ?? null,
      customerId: null,
      isFoundApplication: false,
      todayDate: toLocalISODate(),
      // Address validation state
      isValidatingAddress: false,
      addressValidationResult: null,
      addressValidationDebounce: null,
      title: '',
      selectedFriendPhoneCountry: 'ie',
      friendPhoneNumber: '',
      friendFirstName: '',
      friendLastName: '',
      friendFamilyEmail: '',
      friendRelationship: '',
      countryList: [
        {
          name: 'Canada',
          isTax: false,
          image: require('@/assets/canada.png')
        },
        {
          name: 'Ireland/Europe',
          isTax: true,
          image: require('@/assets/europe.png')
        },
        {
          name: 'Australia',
          isTax: false,
          image: require('@/assets/australia.png')
        },
        {
          name: 'USA/Rest of World',
          isTax: false,
          image: require('@/assets/usa.png')
        },
        {
          name: 'South Africa',
          isTax: false,
          image: require('@/assets/south-africa.png')
        },
        {
          name: 'New Zealand',
          isTax: false,
          image: require('@/assets/new-zealand.png')
        }
      ],
      agentsOptions: [
        { title: 'None', value: '' },
        { title: 'Megan', value: 'Megan' },
        { title: 'MaryClaire', value: 'MaryClaire' },
        { title: 'Mike', value: 'Mike' },
        { title: 'Patrick', value: 'Patrick' },
        { title: 'Loui', value: 'Loui' },
        { title: 'Tomas', value: 'Tomas' },
        { title: 'Sean', value: 'Sean' },
        { title: 'Stephen', value: 'Stephen' }
      ],
      yesNoOptions: ['Yes', 'No'],
      formValid: false,
      formRules: {
        required: (v) => !!v || 'This field is required.',
        email: [(v) => !!v || 'Email required', (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
        friendEmail: [
          (v) => {
            if (v?.length) {
              return /.+@.+\..+/.test(v) || 'E-mail must be valid';
            }
            return true;
          }
        ],
        terms: [(v) => !!v || 'Terms required'],
        onlyDigits: (value) => {
          if (!value) return true; // Allow empty
          return /^\d+$/.test(value) || 'Only digits are allowed';
        }
      }
    };
  },
  async mounted() {
    if (this.$route.name === 'Phone Page') {
      this.isCountrySelected = true;
      this.showStripeLoading();
      try {
        // eslint-disable-next-line no-undef
        this.stripe = await Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        await this.hideStripeLoading();
        this.createAndMountFormElements();
      } catch (err) {
        console.log(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    }
  },
  beforeUnmount() {
    if (this.addressValidationDebounce) {
      clearTimeout(this.addressValidationDebounce);
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    formatAmount(amount) {
      return amount / 100;
    },
    async handleSelectCountry(country) {
      this.isCountrySelected = true;
      this.tax = country.isTax;
      this.countrySelected = country.name;
      this.showStripeLoading();
      try {
        // eslint-disable-next-line no-undef
        this.stripe = await Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        await this.hideStripeLoading();
        this.createAndMountFormElements();
      } catch (err) {
        console.log(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    showStripeLoading() {
      this.isStripeLoaded = false;
    },
    hideStripeLoading() {
      this.isStripeLoaded = true;
    },
    async createAndMountFormElements() {
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', {
        hidePostalCode: true
      });
      this.cardElement.mount('#card-element');

      let options = {
        mode: 'billing',
        fields: {
          phone: 'always',
          name: 'auto'
        },
        display: {
          name: 'split'
        },
        validation: {
          phone: {
            required: 'always'
          }
        }
      };

      let defaultValues = {
        firstName: '',
        lastName: '',
        phone: '',
        address: {
          line1: '',
          city: '',
          state: '',
          country: '',
          postal_code: ''
        }
      };

      const countryCode = this.formatCountryNameToCountryCode(this.countrySelected);
      if (countryCode) {
        defaultValues.address.country = countryCode;
      }

      options = {
        ...options,
        defaultValues: defaultValues
      };
      this.addressElement = elements.create('address', options);
      this.addressElement.mount('#address-element');

      this.cardElement.on('change', this.setValidationCardError);
      this.addressElement.on('change', this.handleAddressChange);
    },
    setValidationCardError(event) {
      this.stripeValidationError = event.error ? event.error.message : '';
    },
    handleAddressChange(event) {
      const { error, address, name, firstName, lastName, phone } = event.value;
      this.stripeValidationError = error ? error?.message : '';
      this.addressValidationError = '';

      if (event.complete) {
        this.shippingAddress = address;
        this.fullName = name;
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
        // Auto-validate address when complete
        this.debounceValidateAddress(address);
      } else {
        this.shippingAddress = null;
        this.fullName = null;
        this.firstName = null;
        this.lastName = null;
        this.phone = null;
        // Reset validation when address is incomplete
        this.addressValidationResult = null;
        this.addressValidationError = '';
      }

      if (address?.country) {
        this.tax = this.isEuropeanCountry(address.country);
        this.countrySelected = address.country;
      }
    },
    debounceValidateAddress(address) {
      if (this.addressValidationDebounce) {
        clearTimeout(this.addressValidationDebounce);
      }

      // Reset validation result while typing
      this.addressValidationResult = null;

      if (!address || !address.line1 || !address.city || !address.country || !address.postal_code) {
        return;
      }

      this.addressValidationDebounce = setTimeout(() => {
        this.validateAddress(address);
      }, 800);
    },
    async validateAddress(address) {
      if (!address || !address.line1 || !address.city || !address.country || !address.postal_code) {
        return;
      }

      this.isValidatingAddress = true;
      this.addressValidationError = '';

      try {
        const addressObj = {
          address1: address.line1 || '',
          address2: address.line2 || '',
          city: address.city || '',
          state: address.state?.trim() || '',
          postcode: address.postal_code || '',
          country: address.country || ''
        };

        const result = await AddressService.autoVerifyAddress(addressObj, addressObj.country || 'GBR');

        if (result.success) {
          this.addressValidationResult = {
            isValid: result.isValid,
            message: result.message,
            verificationStatus: result.verificationStatus,
            avc: result.avc,
            aqi: result.aqi,
            matchScore: result.matchScore,
            cleansedAddress: result.verificationDetails?.verifiedAddressReference || null
          };

          if (!result.isValid) {
            this.addressValidationError = 'Please check your address and try again.';
          }
        } else {
          this.addressValidationResult = {
            isValid: false,
            message: result.message || 'Address verification failed'
          };
          this.addressValidationError = 'Address could not be verified. Please check your address.';
        }
      } catch (error) {
        console.error('Address validation error:', error);
        this.addressValidationResult = {
          isValid: false,
          message: 'Address validation service unavailable'
        };
        this.addressValidationError = 'Address validation temporarily unavailable. Please try again.';
      } finally {
        this.isValidatingAddress = false;
      }
    },
    async pay() {
      try {
        // Validate address before payment
        if (this.shippingAddress && !this.addressValidationResult) {
          await this.validateAddress(this.shippingAddress);
        }

        // Check if address is valid (skip validation requirement if not validated yet)
        if (this.addressValidationResult && !this.addressValidationResult.isValid) {
          this.addressValidationError = 'Please correct your address before proceeding.';
          return;
        }

        if (this.formValid === true) {
          if (this.fullName && this.phone && this.shippingAddress && this.email) {
            this.showPayLoading();
            const result = await this.stripe.createPaymentMethod({
              type: 'card',
              card: this.cardElement,
              billing_details: {
                name: this.fullName,
                email: this.email,
                phone: this.phone,
                address: this.shippingAddress
              }
            });

            this.stripePaymentMethodHandler(result);
          } else {
            this.stripeValidationError = 'Fill in your personal data!';
          }
        }
      } catch (err) {
        this.hidePayLoading();
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async stripePaymentMethodHandler(result) {
      try {
        const { error } = result;
        if (error) {
          this.stripeValidationError = error?.message ?? '';
          this.hidePayLoading();
        } else {
          const res = await StripeService.sendFormData({
            payment_method_id: result.paymentMethod.id,
            customerId: this.customerId,
            currency: this.currencyCode,
            amount: this.amount,
            email: this.email,
            setup_future_usage: false,
            shiping_details: {
              name: this.fullName,
              phone: this.phone,
              address: this.shippingAddress
            },
            isMoto: this.$route.name === 'Phone Page' ? true : false,
            howDidYouHearAboutUs: this.howDidYouHearAboutUs,
            agent: this.agent,
            leadId: this.leadId,
            claim: this.buildClaimPayload()
          });
          const paymentResponse = res;
          this.handleServerResponse(paymentResponse);
        }
      } catch (err) {
        this.hidePayLoading();
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async handleServerResponse(response) {
      try {
        const { error, requires_action, payment_intent_client_secret } = response;
        if (error) {
          this.showSnackbar({ text: error, color: 'red' });
        } else if (requires_action) {
          const { error: errorAction, paymentIntent } = await this.stripe.handleCardAction(
            payment_intent_client_secret
          );
          if (errorAction) {
            this.stripeValidationError = errorAction.message ?? '';
            this.showSnackbar({ text: errorAction.message, color: 'red' });
          } else {
            const serverResponse = await StripeService.sendFormData({
              payment_intent_id: paymentIntent.id
            });
            this.handleServerResponse(await serverResponse);
          }
        } else {
          const { id, status, paymentMethod, customer, latest_charge, applicationId } = response;
          this.customer = customer;
          this.latest_charge = latest_charge;
          if (status === 'Success' && id && paymentMethod) {
            this.isPayCompleted = true;
            this.payCompletedId = id;
            if (applicationId) {
              // The server already created the claim from the confirmed PaymentIntent
              this.isAppCreateError = false;
              this.applicationId = applicationId;
            } else {
              // Claim not created server-side (e.g. details too large for Stripe metadata): create it, Salesforce verifies the payment and is idempotent
              this.createApplication(id, paymentMethod, customer);
            }
          }
        }
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.hidePayLoading();
      }
    },
    // Claim details only; the payment facts (id, amount, currency, customer, method) come from Stripe
    buildClaimPayload() {
      return {
        email: this.email,
        name: this.fullName,
        firstName: this.firstName,
        lastName: this.lastName,
        phone: this.phone,
        address: {
          street: this.shippingAddress.line1 + (this.shippingAddress.line2 ? ', ' + this.shippingAddress.line2 : ''),
          postalCode: this.shippingAddress.postal_code ?? '',
          state: this.shippingAddress.state ?? '',
          city: this.shippingAddress.city ?? '',
          country: this.shippingAddress.country ?? ''
        },
        howDidYouHearAboutUs: this.howDidYouHearAboutUs,
        agent: this.agent,
        title: this.title,
        friendFirstName: this.friendFirstName,
        friendLastName: this.friendLastName,
        friendFamilyEmail: this.friendFamilyEmail,
        friendTel: this.friendTel,
        friendRelationship: this.friendRelationship,
        selectedFriendPhoneCountry: this.friendTel ? this.selectedFriendPhoneCountry : ''
      };
    },
    async createApplication(id, paymentMethod, customer) {
      this.showCreateAppLoading();
      try {
        const { status, applicationId } = await ApplicationService.createApplication({
          id: id,
          amount: this.amount,
          currencyCode: this.currencyCode,
          paymentMethod: paymentMethod,
          customer: customer ?? null,
          latest_charge: this.latest_charge,
          ...this.buildClaimPayload()
        });
        if (status === 'success') {
          this.isAppCreateError = false;
          this.applicationId = applicationId;
        } else {
          this.isAppCreateError = true;
        }
      } catch (err) {
        console.error(err);
        this.isAppCreateError = true;
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.hideCreateAppLoading();
      }
    },
    showPayLoading() {
      this.isPayLoading = true;
    },
    hidePayLoading() {
      this.isPayLoading = false;
    },
    showCreateAppLoading() {
      this.isAppCreateLoading = true;
    },
    hideCreateAppLoading() {
      this.isAppCreateLoading = false;
    },
    isEuropeanCountry(countryCode) {
      const europeanCountryCodes = [
        'AL',
        'AD',
        'AM',
        'AT',
        'AZ',
        'BY',
        'BE',
        'BA',
        'BG',
        'CH',
        'CY',
        'CZ',
        'DE',
        'DK',
        'EE',
        'ES',
        'FI',
        'FR',
        'GE',
        'GR',
        'HR',
        'HU',
        'IE',
        'IS',
        'IT',
        'KZ',
        'LI',
        'LT',
        'LU',
        'LV',
        'MC',
        'MD',
        'ME',
        'MK',
        'MT',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'RU',
        'RS',
        'SE',
        'SI',
        'SK',
        'SM',
        'TR',
        'UA',
        'UK',
        'VA',
        'GI',
        'JE',
        'GG'
      ];
      return europeanCountryCodes.includes(countryCode.toUpperCase());
    },
    formatCountryNameToCountryCode(countryName) {
      let countryCode = '';
      switch (countryName) {
        case 'Ireland/Europe':
          countryCode = 'IE';
          break;
        case 'Canada':
          countryCode = 'CA';
          break;
        case 'USA/Rest of World':
          countryCode = 'US';
          break;
        case 'Australia':
          countryCode = 'AU';
          break;
        case 'New Zealand':
          countryCode = 'NZ';
          break;
        case 'South Africa':
          countryCode = 'ZA';
          break;
        default:
          countryCode = '';
      }
      return countryCode;
    }
  }
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.address-validation-chip {
  font-size: 11px;
  font-weight: 600;
}

:deep(.v-chip--size-small) {
  font-size: 11px;
  height: 24px;
}

.text-caption {
  font-size: 12px;
}

.info-container.info-container__red {
  background-color: #fde8e8;
  border: 1px solid #f5c6c6;
  border-radius: 4px;
  padding: 8px 12px;
  color: #721c24;
}

.info-container.info-container__red p {
  margin: 0;
  font-size: 14px;
}
</style>
